import { Head, Link } from '@inertiajs/react';
import { Check, Trophy } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import type { VotesIndex } from '@/types';
import { getInitials, isDefaultLogo } from './components/logo';
import { TrackFilter } from './components/TrackFilter';
import { VoteLayout } from './components/VoteLayout';
import { VotingCountdown } from './components/VotingCountdown';
import { VotingDeadlineBanner } from './components/VotingDeadlineBanner';

export default function Index({
  edition,
  projects,
  tracks,
  selectedTracks,
  votingStartsAt,
  votingEndsAt,
  defaultLogoHash,
  title,
  description,
  ogImageUrl,
}: VotesIndex) {
  const empty = (
    <div className="flex min-h-[400px] items-center justify-center">
      <p className="font-title text-2xl text-primary/60">
        No hay proyectos disponibles para votar aún.
      </p>
    </div>
  );

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        {ogImageUrl && (
          <>
            <meta property="og:title" content={title} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={ogImageUrl} />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:image" content={ogImageUrl} />
          </>
        )}
      </Head>
      <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        {edition.variant === '25' ? (
          <>
            <VotingDeadlineBanner
              votingStartsAt={votingStartsAt}
              votingEndsAt={votingEndsAt}
            />
            <div className="mb-6 flex justify-center">
              <Button
                asChild
                variant="outline"
                size="lg"
                className="animate-button-breathe gap-2 border-2 border-primary px-8 py-6 text-lg hover:text-primary"
              >
                <Link href="/25/vote/results">
                  <Trophy className="h-5 w-5" />
                  ver los ganadores
                </Link>
              </Button>
            </div>
            {projects.length === 0 ? (
              empty
            ) : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
                {projects.map((project) => (
                  <Link
                    key={project.id}
                    href={`/25/vote/${project.slug}`}
                    className="group relative flex flex-col overflow-hidden border-2 border-primary/20 bg-background transition-all hover:border-primary hover:shadow-lg"
                  >
                    {/* Voted Checkmark */}
                    {project.hasVoted && (
                      <div className="absolute top-2 right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-background">
                        <Check className="h-4 w-4" />
                      </div>
                    )}

                    {/* Logo */}
                    <div className="relative flex h-48 items-center justify-center bg-primary/5 p-6">
                      {project.logoUrl &&
                      !isDefaultLogo(project.logoHash, defaultLogoHash) ? (
                        <img
                          src={project.logoUrl}
                          alt={project.name}
                          width={200}
                          height={200}
                          className="h-full w-full object-contain"
                        />
                      ) : (
                        <div className="flex h-32 w-32 items-center justify-center rounded-full bg-primary/10">
                          <span className="font-bold font-title text-4xl text-primary">
                            {getInitials(project.name)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex flex-1 flex-col gap-2 p-4">
                      <h3 className="font-bold font-title text-primary text-xl">
                        {project.name}
                      </h3>

                      {project.trackName && (
                        <span className="inline-block w-fit bg-primary/10 px-2 py-1 font-title text-primary text-sm">
                          {project.trackName}
                        </span>
                      )}
                    </div>

                    {/* Hover effect */}
                    <div className="absolute inset-0 bg-primary opacity-0 transition-opacity group-hover:opacity-5" />
                  </Link>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <VotingCountdown
              votingStartsAt={votingStartsAt}
              votingEndsAt={votingEndsAt}
            />
            {tracks.length > 0 && (
              <div className="mb-6 flex justify-end">
                <TrackFilter
                  tracks={tracks}
                  selected={selectedTracks}
                  votePath={edition.votePath}
                />
              </div>
            )}
            {projects.length === 0 ? (
              empty
            ) : (
              <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">
                {projects.map((project) => (
                  <li key={project.id}>
                    <Link
                      href={`${edition.votePath}/${project.slug}`}
                      className="group relative flex h-full items-center gap-5 border-2 border-primary/20 bg-background p-4 transition-all hover:border-primary hover:shadow-lg sm:gap-6 sm:p-5"
                    >
                      {/* Logo */}
                      <div className="relative flex h-24 w-24 shrink-0 items-center justify-center bg-primary/5 sm:h-28 sm:w-28">
                        {project.logoUrl &&
                        !isDefaultLogo(project.logoHash, defaultLogoHash) ? (
                          <img
                            src={project.logoUrl}
                            alt={project.name}
                            width={120}
                            height={120}
                            className="h-full w-full object-contain p-2"
                          />
                        ) : (
                          <span className="font-bold font-title text-2xl text-primary">
                            {getInitials(project.name)}
                          </span>
                        )}
                        {project.hasVoted && (
                          <div className="-right-1 -top-1 absolute flex h-5 w-5 items-center justify-center rounded-full bg-primary text-background">
                            <Check className="h-3 w-3" />
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="flex min-w-0 flex-1 flex-col gap-1">
                        <h3 className="font-bold font-sans text-lg text-primary leading-tight sm:text-xl">
                          {project.name}
                        </h3>
                        {project.onelinerShort && (
                          <p
                            className="line-clamp-2 text-primary/70 text-sm leading-snug"
                            title={project.onelinerShort}
                          >
                            {project.onelinerShort}
                          </p>
                        )}
                      </div>

                      {/* Track */}
                      {project.trackName && (
                        <span
                          className="absolute top-2 right-3 font-sans text-[10px] text-primary/60 uppercase sm:text-xs"
                          style={{ letterSpacing: '0.2em' }}
                        >
                          {project.trackName}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </>
  );
}

Index.layout = (page: ReactNode) => <VoteLayout>{page}</VoteLayout>;
